//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Components\Survey\Modal\ViewSurveyModal.jsx
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, FileText, CircleCheck, Users, Copy, Check, Search, CopyPlus, Loader2, Bell, Pencil, ArrowLeft, CheckCircle2 } from "lucide-react";
import sidebarImage from "../../../assests/SidebarImage.png";
import SidebarHeaderImage from "../../../assests/SidebarHeaderImage.png";
import PublishSurveyModal from "./PublishSurveyModal";
import { GetSurevyResponders, CreateRemainders, UpdateResponderDetails, ExpiredAssessmentAPI } from "../Services/SurveyService";
import { sanitizeHtml, stripHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../Utility/sanitizeHtml";
import { getChannels } from "../Services/MasterDataService";

const getStatusColor = (status) => {
    switch (status) {
        case "Published":
        case "Active":
            return "bg-green-100 text-green-700";
        case "Scheduled":
            return "bg-purple-100 text-purple-700";
        case "Completed":
            return "bg-blue-100 text-blue-700";
        case "Draft":
        case "Incomplete":
            return "bg-orange-100 text-orange-700";
        case "Inactive":
            return "bg-red-100 text-red-700";
        default:
            return "bg-gray-100 text-gray-700";
    }
};

const ResponderSkeleton = () => (
    <div className="rounded-xl border border-gray-100 bg-white p-3 animate-pulse">
        <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 shrink-0 rounded-full bg-gray-200" />
            <div className="flex-1 space-y-1.5">
                <div className="h-3 w-32 rounded bg-gray-200" />
                <div className="h-2.5 w-40 rounded bg-gray-100" />
            </div>
            <div className="h-5 w-24 shrink-0 rounded-full bg-gray-100" />
        </div>
        <div className="mt-2.5 h-9 w-full rounded-xl bg-gray-100" />
    </div>
);

const ResponderCard = ({ responder, selectable = false, checked = false, onToggle, onEdit }) => {
    const [copied, setCopied] = useState(false);
    const initial = responder.responderName?.trim()?.charAt(0)?.toUpperCase() || "?";

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(responder.assessmentUrl);
            setCopied(true);
            CustomToast("success", "Assessment link copied to clipboard.");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            CustomToast("fail", "Unable to copy the link.");
        }
    };

    const isLocked = selectable && (responder.isRespondCollected || responder.isDeadlineMet);
    return (
        <div
            className={`rounded-xl border p-3 shadow-sm transition ${selectable && checked
                ? "border-[#7f35b2] bg-[#7f35b2]/5"
                : "border-gray-100 bg-white"
                }${isLocked ? "opacity-60" : ""}`}
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                    {selectable && (
                        <input
                            type="checkbox"
                            checked={checked}
                            disabled={responder.isRespondCollected || responder.isDeadlineMet}
                            onChange={() => onToggle?.(responder.responderUID)}
                            title={
                                responder.isRespondCollected
                                    ? "Already completed the assessment"
                                    : responder.isDeadlineMet
                                        ? "Deadline has been met"
                                        : undefined
                            }
                            className="h-4 w-4 shrink-0 accent-[#7f35b2] cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                        />
                    )}
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-[#7f35b2] text-sm font-semibold text-[#7f35b2] select-none">
                        {initial}
                    </span>
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{responder.responderName}</p>
                        <p className="truncate text-xs text-gray-400">{responder.responderMail}</p>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                    <span
                        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-semibold ${responder.isRespondCollected
                            ? "bg-green-100 text-green-700"
                            : responder.isDeadlineMet
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {responder.isRespondCollected
                            ? "Completed"
                            : responder.isDeadlineMet
                                ? "Expired"
                                : "Not Completed"}

                    </span>
                    <span className="whitespace-nowrap rounded-full bg-[#7f35b2]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-[#7f35b2]">
                        {responder.channel} . {responder.experienceType}
                    </span>

                </div>
            </div>

            <div className="mt-2.5 flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2.5">
                <a href={responder.assessmentUrl} rel="noopener noreferrer" target="_blank" className="min-w-0 flex-1 truncate text-xs text-blue-500 pointer-events-auto">{responder.assessmentUrl}</a>
                <button
                    type="button"
                    onClick={handleCopy}
                    className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 transition hover:border-[#7f35b2] hover:text-[#7f35b2]"
                >
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {copied ? "Copied" : "Copy"}
                </button>
                <button
                    type="button"
                    onClick={() => onEdit?.(responder)}
                    title="Edit responder"
                    className="flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white p-1.5 text-gray-600 transition hover:border-[#7f35b2] hover:text-[#7f35b2]"
                >
                    <Pencil size={13} />
                </button>
            </div>
        </div>
    );
};

const EXPERIENCE_TYPE_OPTIONS = ["Experienced", "Fresher"];

const deadlineToInputValue = (deadline) => {
    if (!deadline) return "";
    const [datePart, timePart = "00:00:00"] = deadline.split(" ");
    const [day, month, year] = datePart.split("-");
    const [hh, mm] = timePart.split(":");
    return `${year}-${month}-${day}T${hh}:${mm}`;
};

const inputValueToApiPayload = (value) => (value ? `${value}:00` : null);

const inputValueToDisplayFormat = (value) => {
    if (!value) return null;
    const [datePart, timePart] = value.split("T");
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year} ${timePart}:00`;
};

const nowForInput = () => {
    const d = new Date();
    d.setSeconds(0, 0);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
};

const EditResponderForm = ({ responder, channels, loadingChannels, onCancel, onSave, saving }) => {
    const [formData, setFormData] = useState({
        responderName: responder.responderName || "",
        responderMail: responder.responderMail || "",
        phoneNo: responder.phoneNo || "",
        channelID: responder.channelID || "",
        experienceType: responder.experienceType || "",
        gender: responder.gender || "",
        location: responder.location || "",
        deadline: deadlineToInputValue(responder.deadline),
    });

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const inputClass =
        "mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]/30";
    const labelClass = "text-xs font-semibold text-gray-500 uppercase tracking-wide";

    return (
        <form onSubmit={handleSubmit} className="relative flex flex-1 min-h-0 flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto px-3.5 pt-3.5 space-y-4">
                <div>
                    <label className={labelClass}>Responder Name</label>
                    <input type="text" value={formData.responderName} onChange={handleChange("responderName")} className={inputClass} />
                </div>
                <div>
                    <label className={labelClass}>Email</label>
                    <input type="email" value={formData.responderMail} onChange={handleChange("responderMail")} className={inputClass} />
                </div>
                <div>
                    <label className={labelClass}>Phone No.</label>
                    <input type="tel" value={formData.phoneNo} onChange={handleChange("phoneNo")} className={inputClass} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={labelClass}>Channel</label>
                        <select
                            value={formData.channelID}
                            onChange={handleChange("channelID")}
                            disabled={loadingChannels}
                            className={`${inputClass} bg-white disabled:bg-gray-50 disabled:text-gray-400`}
                        >
                            <option value="" disabled>
                                {loadingChannels ? "Loading channels..." : "Select a channel"}
                            </option>
                            {channels
                                .filter((ch) => ch.isactive)
                                .map((ch) => (
                                    <option key={ch.id} value={ch.id}>
                                        {ch.value}
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Experience Type</label>
                        <select value={formData.experienceType} onChange={handleChange("experienceType")} className={`${inputClass} bg-white`}>
                            <option value="" disabled>Select experience type</option>
                            {EXPERIENCE_TYPE_OPTIONS.map((type) => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={labelClass}>Gender</label>
                        <select value={formData.gender} onChange={handleChange("gender")} className={`${inputClass} bg-white`}>
                            <option value="">Select</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Others">Others</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Location</label>
                        <input type="text" value={formData.location} onChange={handleChange("location")} className={inputClass} />
                    </div>
                </div>
                <div>
                    <label className={labelClass}>Deadline</label>
                    <input
                        type="datetime-local"
                        value={formData.deadline}
                        min={nowForInput()}
                        onChange={handleChange("deadline")}
                        className={inputClass}
                    />
                </div>
            </div>
            <div className="flex shrink-0 justify-end gap-3 border-t px-3.5 py-3.5">
                <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition">
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-[#7f35b2] hover:bg-[#9e0085] text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving && <Loader2 size={14} className="animate-spin" />}
                    {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>
        </form>
    );
};

const ViewSurveyModal = ({ isOpen, survey, onClose, showPublish, onPublish, onDuplicate, showDuplicate = true, onMarkAsComplete }) => {
    const [displaySurvey, setDisplaySurvey] = useState(survey);
    const [publishModalOpen, setPublishModalOpen] = useState(false);
    const [responders, setResponders] = useState([]);
    const [respondersLoading, setRespondersLoading] = useState(false);
    const [responderSearch, setResponderSearch] = useState("");
    const [duplicating, setDuplicating] = useState(false);
    const [markingComplete, setMarkingComplete] = useState(false);
    const [confirmingComplete, setConfirmingComplete] = useState(false);
    const [reminderMode, setReminderMode] = useState(false);
    const [selectedResponders, setSelectedResponders] = useState(new Set());
    const [sendingReminder, setSendingReminder] = useState(false);
    const [showResponders, setShowResponders] = useState(false);
    const [editingResponder, setEditingResponder] = useState(null);
    const [savingResponder, setSavingResponder] = useState(false);
    const [channels, setChannels] = useState([]);
    const [loadingChannels, setLoadingChannels] = useState(false);

    useEffect(() => {
        if (survey) setDisplaySurvey(survey);
    }, [survey]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "auto";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) setPublishModalOpen(false);
    }, [isOpen]);

    useEffect(() => {
        if (!survey?.uniqueKey) {
            setResponders([]);
            return;
        }
        let isMounted = true;
        setRespondersLoading(true);
        GetSurevyResponders(survey.uniqueKey)
            .then((res) => {
                if (!isMounted) return;
                if (res.data.status?.toLowerCase() === "success") {
                    setResponders(res.data.responders || []);
                } else {
                    setResponders([]);
                }
            })
            .catch(() => {
                if (isMounted) setResponders([]);
            })
            .finally(() => {
                if (isMounted) setRespondersLoading(false);
            });
        return () => {
            isMounted = false;
        };
    }, [survey?.uniqueKey]);

    useEffect(() => {
        setResponderSearch("");
        setDuplicating(false);
        setMarkingComplete(false);
        setConfirmingComplete(false);
        setReminderMode(false);
        setSelectedResponders(new Set());
        setShowResponders(false);
        setEditingResponder(null);
    }, [survey?.uniqueKey]);


    useEffect(() => {
        if (!isOpen) return;

        const fetchChannels = async () => {
            try {
                setLoadingChannels(true);
                const response = await getChannels();
                if (response.data.status?.toLowerCase() === "success") {
                    setChannels(response.data.channels || []);
                } else {
                    CustomToast("fail", response.data.message || "Unable to load channels.");
                }
            } catch (error) {
                CustomToast("fail", error.response?.data?.message || "Unable to load channels.");
            } finally {
                setLoadingChannels(false);
            }
        };

        fetchChannels();
    }, [isOpen]);

    const filteredResponders = responders.filter((r) => {
        const q = responderSearch.trim().toLowerCase();
        if (!q) return true;
        return (
            r.responderName?.toLowerCase().includes(q) ||
            r.responderMail?.toLowerCase().includes(q) ||
            r.channel?.toLowerCase().includes(q) ||
            r.experienceType?.toLowerCase().includes(q)
        );
    });

    const handleDuplicateClick = async () => {
        if (duplicating || !displaySurvey?.uniqueKey) return;
        setDuplicating(true);
        try {
            await onDuplicate(displaySurvey.uniqueKey);
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "Assessment duplicated failed.");
        } finally {
            setDuplicating(false);
        }
    };

    const handleMarkAsCompleteClick = () => {
        setConfirmingComplete(true);
    };

    const confirmMarkAsComplete = async () => {
        if (markingComplete || !displaySurvey?.uniqueKey) return;
        setMarkingComplete(true);
        try {
            const res = await ExpiredAssessmentAPI(displaySurvey.uniqueKey);
            if (res.data.status?.toLowerCase() === "success") {
                CustomToast("success", res.data.message || "Assessment marked as complete.");
                if (typeof onMarkAsComplete === 'function') {
                    await onMarkAsComplete();
                } else {
                    onClose();
                }
            } else {
                CustomToast("fail", res.data.message || "Failed to mark as complete.");
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "Failed to mark as complete.");
        } finally {
            setMarkingComplete(false);
            setConfirmingComplete(false);
        }
    };

    const handleReminderToggle = () => {
        if (reminderMode) {
            // turning selection mode off -> clear selections
            setReminderMode(false);
            setSelectedResponders(new Set());
        } else {
            setReminderMode(true);
            setShowResponders(true);
        }
    };

    const handleResponderCheckToggle = (responderUID) => {
        setSelectedResponders((prev) => {
            const next = new Set(prev);
            if (next.has(responderUID)) {
                next.delete(responderUID);
            } else {
                next.add(responderUID);
            }
            return next;
        });
    };

    const handleEditResponder = (responder) => {
        setEditingResponder(responder);
    };

    const handleCancelEdit = () => {
        setEditingResponder(null);
    };

    const handleSaveResponder = async (formData) => {
        if (!editingResponder) return;
        setSavingResponder(true);
        try {
            const payload = {
                responderUID: editingResponder.responderUID,
                assessmentUID: displaySurvey.uniqueKey,
                responderName: formData.responderName,
                responderMail: formData.responderMail,
                responderPhoneNo: formData.phoneNo,
                channelID: Number(formData.channelID),
                experianceType: formData.experienceType,
                deadline: inputValueToApiPayload(formData.deadline),
                gender: formData.gender,
                location: formData.location,
            };
            const res = await UpdateResponderDetails(payload);
            if (res.data.status?.toLowerCase() === "success") {
                CustomToast("success", res.data.message || "Responder updated successfully.");
                setResponders((prev) =>
                    prev.map((r) =>
                        r.responderUID === editingResponder.responderUID
                            ? { ...r, ...formData, phoneNo: formData.phoneNo, deadline: inputValueToDisplayFormat(formData.deadline) }
                            : r
                    )
                );
                setEditingResponder(null);
            } else {
                CustomToast("fail", res.data.message || "Failed to update responder.");
            }
        } catch (error) {
            CustomToast("fail", error?.response?.data?.message || "Failed to update responder.");
        } finally {
            setSavingResponder(false);
        }
    };

    const handleSendReminderClick = async () => {
        const selectedUIDs = Array.from(selectedResponders);
        if (selectedUIDs.length === 0) {
            CustomToast("fail", "Select at least one responder to send a reminder.");
            return;
        }
        if (sendingReminder || !displaySurvey?.uniqueKey) return;

        setSendingReminder(true);
        try {
            const res = await CreateRemainders({
                assessmentUID: displaySurvey.uniqueKey,
                responderUID: selectedUIDs,
            });
            if (res.data.status?.toLowerCase() === "success") {
                CustomToast("success", res.data.message || "Reminder sent successfully.");
                setReminderMode(false);
                setSelectedResponders(new Set());
            } else {
                CustomToast("fail", res.data.message || "Failed to send reminder.");
            }
        } catch (error) {
            CustomToast("fail", error.response.data.message || "Failed to send reminder.");
        } finally {
            setSendingReminder(false);
        }
    };


    if (!displaySurvey) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-slate-900/40 backdrop-blur-[2px]
                        transition-opacity duration-300 z-[9998]
                        ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            />

            {/* Drawer */}
            <div
                className={`fixed top-0 right-0 h-full w-full md:w-[640px]
                    bg-white shadow-2xl z-[9999]
                    rounded-l-2xl overflow-hidden
                    transform transition-transform duration-300 ease-in-out
                    flex flex-col
                    ${isOpen ? "translate-x-0" : "translate-x-full"}`}
            >
                <div className="relative overflow-hidden flex items-center justify-between px-6 py-5 border-b bg-green-50 shrink-0">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-cover bg-center pointer-events-none"
                        style={{ backgroundImage: `url(${SidebarHeaderImage})` }}
                    />
                    <div className="absolute inset-0 bg-black/35 pointer-events-none" />
                    <div className="relative z-10 flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                            <div className="bg-[#7f35b2]/30 p-2.5 rounded-xl shrink-0">
                                <FileText className="text-white" size={22} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-100">Assessment Details</h2>
                                <p className="text-sm text-gray-100 mt-0.5">View Assessment information</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowResponders((prev) => !prev)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition ${showResponders
                                    ? "bg-white/90 text-[#7f35b2] hover:bg-white"
                                    : "bg-white/20 text-gray-100 hover:bg-white/30"
                                    }`}
                            >
                                <Users size={14} />
                                {showResponders ? "Hide Responders" : "View Responder"}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="relative flex-1 min-h-0 overflow-hidden flex flex-col">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-right-bottom opacity-[0.07] pointer-events-none"
                        style={{
                            backgroundImage: `url(${sidebarImage})`,
                            backgroundSize: "360px auto",
                        }}
                    />

                    {!showResponders && (
                        <div className="relative shrink-0 p-3.5 pb-0 space-y-6">
                            <div>
                                <div className="flex gap-3">
                                    <p className="text-xl font-bold text-gray-900">{displaySurvey.surveyName}</p>
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(displaySurvey.status)}`}>
                                        {displaySurvey.status === "Incomplete" ? "Draft" : displaySurvey.status}
                                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                    </span>
                                </div>
                                <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                                <div
                                    className="description-rich-content text-gray-500 text-sm mt-1 line-clamp-5"
                                    title={stripHtml(displaySurvey.surveyDescription)}
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(displaySurvey.surveyDescription) }}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-400">Questions</p>
                                    <p className="text-lg font-semibold text-gray-900">{displaySurvey.totalQuestions}</p>
                                </div>
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-400">Responses</p>
                                    <p className="text-lg font-semibold text-gray-900">{displaySurvey.responses ?? 0}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    {showResponders && !editingResponder && (
                        <div className="relative flex-1 min-h-0 flex flex-col px-3.5 pt-3.5 pb-0">
                            <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                    <Users size={13} className="text-[#7f35b2]" />
                                    Responders {responders.length > 0 && `(${responders.length})`}
                                </div>

                                {responders.length > 0 && (
                                    <div className="relative">
                                        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={responderSearch}
                                            onChange={(e) => setResponderSearch(e.target.value)}
                                            placeholder="Search"
                                            className="w-72 rounded-full border border-gray-200 bg-gray-50 py-1 pl-7 pr-2.5 text-xs text-[#7f35b2] outline-none transition focus:w-66 focus:border-[#7f35b2] focus:bg-white focus:ring-1 focus:ring-[#7f35b2]/30"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto space-y-2.5 pr-1">
                                {respondersLoading &&
                                    Array.from({ length: 3 }).map((_, i) => <ResponderSkeleton key={i} />)}

                                {!respondersLoading && responders.length === 0 && (
                                    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-6 text-center">
                                        <p className="text-sm text-gray-400">No responders added yet.</p>
                                    </div>
                                )}

                                {!respondersLoading && responders.length > 0 && filteredResponders.length === 0 && (
                                    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-6 text-center">
                                        <p className="text-sm text-gray-400">No responders match "{responderSearch}".</p>
                                    </div>
                                )}

                                {!respondersLoading &&
                                    filteredResponders.map((responder) => (
                                        <ResponderCard key={responder.responderUID} responder={responder} selectable={reminderMode}
                                            checked={selectedResponders.has(responder.responderUID)}
                                            onToggle={handleResponderCheckToggle}
                                            onEdit={handleEditResponder} />
                                    ))}
                            </div>
                        </div>
                    )}
                    {showResponders && editingResponder && (
                        <div className="relative flex-1 min-h-0 flex flex-col">
                            <div className="flex shrink-0 items-center gap-2 px-3.5 pt-3.5 pb-3">
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="flex items-center justify-center rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:border-[#7f35b2] hover:text-[#7f35b2] transition"
                                >
                                    <ArrowLeft size={15} />
                                </button>
                                <div className="text-sm font-semibold text-gray-900">
                                    Edit — {editingResponder.responderName}
                                </div>
                            </div>
                            <EditResponderForm
                                responder={editingResponder}
                                channels={channels}
                                loadingChannels={loadingChannels}
                                onCancel={handleCancelEdit}
                                onSave={handleSaveResponder}
                                saving={savingResponder}
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t bg-white px-6 py-4 flex justify-end gap-3 shrink-0">
                    {showResponders && responders.length > 0 && (
                        <button
                            onClick={handleReminderToggle}
                            className={`flex items-center gap-2 px-5 py-2.5 border rounded-xl transition ${reminderMode
                                ? "border-gray-300 text-gray-700 hover:bg-gray-50"
                                : "border-[#7f35b2] text-[#7f35b2] hover:bg-[#7f35b2]/5"
                                }`}
                        >
                            {!reminderMode && <Bell size={16} />}
                            {reminderMode ? "Cancel" : "Reminder"}
                        </button>
                    )}

                    {reminderMode && (
                        <button
                            onClick={handleSendReminderClick}
                            disabled={selectedResponders.size === 0 || sendingReminder}
                            className="flex items-center gap-2 px-5 py-2.5 bg-[#7f35b2] hover:bg-[#9e0085] text-white rounded-xl transition whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {sendingReminder ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
                            {sendingReminder
                                ? "Sending..."
                                : `Send Reminder ${selectedResponders.size > 0 ? `(${selectedResponders.size})` : ""}`}
                        </button>
                    )}
                    {showDuplicate && (
                        <button
                            onClick={handleDuplicateClick}
                            disabled={duplicating || markingComplete}
                            className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 transition text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {duplicating ? <Loader2 size={16} className="animate-spin" /> : <CopyPlus size={16} />}
                            {duplicating ? "Duplicating..." : "Duplicate"}
                        </button>
                    )}
                    {(displaySurvey.status === "Active" || displaySurvey.status === "Scheduled to start" || displaySurvey.status === "Scheduled") && (
                        <button
                            onClick={handleMarkAsCompleteClick}
                            disabled={markingComplete || duplicating}
                            className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 transition text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {markingComplete ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                            {markingComplete ? "Marking..." : "Mark as Complete"}
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 transition"
                    >
                        Close
                    </button>
                    {showPublish && (
                        <button
                            onClick={() => setPublishModalOpen(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-[#7f35b2] hover:bg-[#9e0085] text-white rounded-xl transition"
                        >
                            <CircleCheck size={16} />
                            Publish
                        </button>
                    )}
                </div>
            </div>
            {createPortal(
                <PublishSurveyModal
                    isOpen={publishModalOpen}
                    survey={displaySurvey}
                    onClose={() => setPublishModalOpen(false)}
                    onPublish={onPublish}
                />,
                document.body
            )}

            {/* Mark as Complete Confirmation Modal */}
            {confirmingComplete && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300">
                    <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden transform transition-transform duration-300 scale-100">
                        {/* Header */}
                        <div className="flex items-center justify-between gap-3 bg-[#7f35b2]/10 px-6 py-4">
                            <div className="flex min-w-0 items-center gap-3">
                                <div className="bg-white p-2 rounded-xl shrink-0">
                                    <FileText className="text-[#7f35b2]" size={20} />
                                </div>
                                <h2
                                    className="truncate text-lg font-bold text-gray-900"
                                    title={displaySurvey?.surveyName}
                                >
                                    {displaySurvey?.surveyName}
                                </h2>
                            </div>
                            <button
                                onClick={() => setConfirmingComplete(false)}
                                disabled={markingComplete}
                                className="shrink-0 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex items-start gap-4 px-6 py-6">
                            <div className="bg-[#7f35b2]/10 p-3 rounded-full shrink-0">
                                <CheckCircle2 className="text-[#7f35b2]" size={24} />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Are you sure you want to mark this assessment as complete?
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Responders will no longer be able to submit their assessments and you will not be able to revert this change. Please make sure you have reviewed all responses before proceeding.
                                </p>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 border-t px-6 py-4">
                            <button
                                onClick={() => setConfirmingComplete(false)}
                                disabled={markingComplete}
                                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmMarkAsComplete}
                                disabled={markingComplete}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#7f35b2] hover:bg-[#9e0085] rounded-xl transition disabled:opacity-60"
                            >
                                {markingComplete ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                {markingComplete ? "Marking..." : "Yes, Mark Complete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ViewSurveyModal;


